import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Search, Filter, Download, Ban, CheckCircle, XCircle, RefreshCw, Calendar, Globe, Code } from 'lucide-react';

interface Installation {
  id: string;
  installation_key: string;
  domain: string;
  url: string;
  wordpress_version: string;
  php_version: string;
  plugin_version: string;
  status: 'active' | 'suspended' | 'revoked';
  last_update_check: string | null;
  created_at: string;
  license_id: string | null;
}

interface Filters {
  status: string;
  version: string;
  search: string;
}

export function InstallationsManagement() {
  const navigate = useNavigate();
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [filteredInstallations, setFilteredInstallations] = useState<Installation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<Filters>({
    status: 'all',
    version: 'all',
    search: '',
  });
  const [selectedInstallations, setSelectedInstallations] = useState<string[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    suspended: 0,
    revoked: 0,
    stale: 0,
  });

  useEffect(() => {
    checkAuth();
    loadInstallations();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [installations, filters]);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/login');
    }
  };

  const loadInstallations = async () => {
    try {
      const { data, error } = await supabase
        .from('plugin_installations')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setInstallations(data || []);

      const now = new Date();
      const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

      const calculatedStats = {
        total: data?.length || 0,
        active: data?.filter(i => i.status === 'active').length || 0,
        suspended: data?.filter(i => i.status === 'suspended').length || 0,
        revoked: data?.filter(i => i.status === 'revoked').length || 0,
        stale: data?.filter(i => {
          const lastCheck = i.last_update_check ? new Date(i.last_update_check) : new Date(i.created_at);
          return lastCheck < sixtyDaysAgo && i.status === 'active';
        }).length || 0,
      };

      setStats(calculatedStats);
    } catch (error) {
      console.error('Error loading installations:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...installations];

    if (filters.status !== 'all') {
      if (filters.status === 'stale') {
        const now = new Date();
        const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(i => {
          const lastCheck = i.last_update_check ? new Date(i.last_update_check) : new Date(i.created_at);
          return lastCheck < sixtyDaysAgo && i.status === 'active';
        });
      } else {
        filtered = filtered.filter(i => i.status === filters.status);
      }
    }

    if (filters.version !== 'all') {
      filtered = filtered.filter(i => i.plugin_version === filters.version);
    }

    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(i =>
        i.domain.toLowerCase().includes(search) ||
        i.url.toLowerCase().includes(search) ||
        i.installation_key.toLowerCase().includes(search)
      );
    }

    setFilteredInstallations(filtered);
  };

  const updateInstallationStatus = async (installationIds: string[], newStatus: 'active' | 'suspended' | 'revoked') => {
    if (!confirm(`Are you sure you want to ${newStatus} ${installationIds.length} installation(s)?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('plugin_installations')
        .update({ status: newStatus })
        .in('id', installationIds);

      if (error) throw error;

      await loadInstallations();
      setSelectedInstallations([]);
      alert(`Successfully ${newStatus} ${installationIds.length} installation(s)`);
    } catch (error) {
      console.error('Error updating installations:', error);
      alert('Failed to update installations');
    }
  };

  const exportToCSV = () => {
    const headers = ['Domain', 'Site URL', 'WordPress Version', 'PHP Version', 'Plugin Version', 'Status', 'Last Update Check', 'Created'];
    const rows = filteredInstallations.map(i => [
      i.domain,
      i.url,
      i.wordpress_version,
      i.php_version,
      i.plugin_version,
      i.status,
      i.last_update_check ? new Date(i.last_update_check).toLocaleDateString() : 'Never',
      new Date(i.created_at).toLocaleDateString(),
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plugin-installations-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getVersions = () => {
    return [...new Set(installations.map(i => i.plugin_version))].sort();
  };

  const getDaysSinceLastCheck = (installation: Installation) => {
    const lastCheck = installation.last_update_check ? new Date(installation.last_update_check) : new Date(installation.created_at);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - lastCheck.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading installations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Plugin Installations</h1>
          <p className="mt-2 text-gray-600">Manage all WordPress plugin installations</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Globe className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Suspended</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.suspended}</p>
              </div>
              <Ban className="h-8 w-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Revoked</p>
                <p className="text-2xl font-bold text-red-600">{stats.revoked}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Stale (60+ days)</p>
                <p className="text-2xl font-bold text-orange-600">{stats.stale}</p>
              </div>
              <Calendar className="h-8 w-8 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by domain, URL, or installation key..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="revoked">Revoked</option>
                  <option value="stale">Stale</option>
                </select>

                <select
                  value={filters.version}
                  onChange={(e) => setFilters({ ...filters, version: e.target.value })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Versions</option>
                  {getVersions().map(version => (
                    <option key={version} value={version}>{version}</option>
                  ))}
                </select>

                <button
                  onClick={exportToCSV}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Export CSV
                </button>

                <button
                  onClick={loadInstallations}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </button>
              </div>
            </div>

            {selectedInstallations.length > 0 && (
              <div className="mt-4 flex items-center gap-3">
                <span className="text-sm text-gray-600">{selectedInstallations.length} selected</span>
                <button
                  onClick={() => updateInstallationStatus(selectedInstallations, 'active')}
                  className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                >
                  Activate
                </button>
                <button
                  onClick={() => updateInstallationStatus(selectedInstallations, 'suspended')}
                  className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                >
                  Suspend
                </button>
                <button
                  onClick={() => updateInstallationStatus(selectedInstallations, 'revoked')}
                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                >
                  Revoke
                </button>
                <button
                  onClick={() => setSelectedInstallations([])}
                  className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                >
                  Clear Selection
                </button>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedInstallations.length === filteredInstallations.length && filteredInstallations.length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedInstallations(filteredInstallations.map(i => i.id));
                        } else {
                          setSelectedInstallations([]);
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Domain
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Version
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Check
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registered
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInstallations.map((installation) => (
                  <tr key={installation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedInstallations.includes(installation.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedInstallations([...selectedInstallations, installation.id]);
                          } else {
                            setSelectedInstallations(selectedInstallations.filter(id => id !== installation.id));
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{installation.domain}</div>
                        <div className="text-sm text-gray-500">{installation.url}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">Plugin: {installation.plugin_version}</div>
                        <div className="text-gray-500">WP: {installation.wordpress_version}</div>
                        <div className="text-gray-500">PHP: {installation.php_version}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        installation.status === 'active' ? 'bg-green-100 text-green-800' :
                        installation.status === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {installation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {installation.last_update_check
                          ? new Date(installation.last_update_check).toLocaleDateString()
                          : 'Never'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {getDaysSinceLastCheck(installation)} days ago
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {new Date(installation.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredInstallations.length === 0 && (
              <div className="text-center py-12">
                <Code className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No installations found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filters.search || filters.status !== 'all' || filters.version !== 'all'
                    ? 'Try adjusting your filters'
                    : 'No plugin installations registered yet'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
